import {
  faCheck,
  faChevronLeft,
  faPencil,
  faPlus,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RefObject, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOutsideClick } from "../../util/handlers";
import { useProjects } from "../state";
import { Tag, TagColor } from "../state/types";
import { TAG_COLORS, tagBg, tagHover, tagText } from "./tagColors";

const POPOVER_WIDTH = 288;
const POPOVER_MARGIN = 8;

type Props = {
  parcel: { id: number; tags: Tag[] };
  projectId: number;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
};

type Mode = { kind: "list" } | { kind: "create" } | { kind: "edit"; tag: Tag };

export const TagsPopover = ({ parcel, projectId, anchorRef, onClose }: Props) => {
  const tags = useProjects((s) => s.tags) ?? [];
  const createTag = useProjects((s) => s.createTag);
  const updateTag = useProjects((s) => s.updateTag);
  const deleteTag = useProjects((s) => s.deleteTag);
  const setParcelTags = useProjects((s) => s.setParcelTags);

  const ref = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>({ kind: "list" });
  const [query, setQuery] = useState("");
  const [dragId, setDragId] = useState<number | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popoverHeight = ref.current?.offsetHeight ?? 320;
      let left = rect.right - POPOVER_WIDTH;
      if (left < POPOVER_MARGIN) left = POPOVER_MARGIN;
      if (left + POPOVER_WIDTH > window.innerWidth - POPOVER_MARGIN) {
        left = window.innerWidth - POPOVER_WIDTH - POPOVER_MARGIN;
      }
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow >= popoverHeight + POPOVER_MARGIN || spaceBelow >= rect.top
          ? rect.bottom + 4
          : Math.max(POPOVER_MARGIN, rect.top - popoverHeight - 4);
      setPos({ top, left });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef, mode]);

  useOutsideClick([ref, anchorRef], onClose);

  const appliedIds = useMemo(() => parcel.tags.map((t) => t.id), [parcel.tags]);
  const appliedSet = useMemo(() => new Set(appliedIds), [appliedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(q));
  }, [tags, query]);

  const toggle = (tag: Tag) => {
    const next = appliedSet.has(tag.id)
      ? appliedIds.filter((id) => id !== tag.id)
      : [...appliedIds, tag.id];
    setParcelTags(projectId, parcel.id, next);
  };

  const reorder = (fromId: number, toId: number) => {
    if (fromId === toId) return;
    const from = appliedIds.indexOf(fromId);
    const to = appliedIds.indexOf(toId);
    if (from < 0 || to < 0) return;
    const next = [...appliedIds];
    next.splice(from, 1);
    next.splice(to, 0, fromId);
    setParcelTags(projectId, parcel.id, next);
  };

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: POPOVER_WIDTH,
        visibility: pos ? "visible" : "hidden",
      }}
      className="z-50 bg-white rounded-md shadow-lg border border-gray-200"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b">
        {mode.kind === "list" ? (
          <span className="text-sm font-semibold">Labels</span>
        ) : (
          <button
            onClick={() => setMode({ kind: "list" })}
            className="text-sm text-gray-600 hover:text-black inline-flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
            {mode.kind === "create" ? "Nieuw label" : "Label bewerken"}
          </button>
        )}
        <button onClick={onClose} className="text-gray-400 hover:text-black">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {mode.kind === "list" && (
        <div className="p-2 space-y-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek labels..."
            className="w-full px-2 py-1 text-sm border rounded"
          />
          {parcel.tags.length > 0 && (
            <div className="text-[10px] uppercase text-gray-400 px-1">
              Op dit perceel (sleep om te ordenen)
            </div>
          )}
          {parcel.tags.length > 0 && (
            <ul className="space-y-1">
              {parcel.tags.map((tag) => (
                <li
                  key={tag.id}
                  draggable
                  onDragStart={() => setDragId(tag.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragId != null) reorder(dragId, tag.id);
                    setDragId(null);
                  }}
                  className={`flex items-center gap-2 px-1.5 py-1 rounded cursor-grab active:cursor-grabbing ${dragId === tag.id ? "opacity-40" : ""}`}
                  title="Sleep om volgorde te wijzigen"
                >
                  <span className="text-gray-300 select-none">⋮⋮</span>
                  <span
                    className={`flex-1 min-w-0 truncate px-2 py-1 rounded text-xs font-semibold ${tagBg[tag.color]} ${tagText[tag.color]}`}
                  >
                    {tag.name}
                  </span>
                  <button
                    onClick={() => toggle(tag)}
                    className="text-gray-400 hover:text-red-500 px-1"
                    aria-label={`Tag ${tag.name} loskoppelen`}
                  >
                    <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="text-[10px] uppercase text-gray-400 px-1 pt-1">Beschikbaar</div>
          <ul className="space-y-1 max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="text-xs text-gray-400 px-2 py-1">Geen labels</li>
            )}
            {filtered.map((tag) => (
              <li key={tag.id} className="flex items-center gap-2">
                <button
                  onClick={() => toggle(tag)}
                  className="flex items-center gap-2 flex-1 min-w-0 px-1.5 py-1 rounded hover:bg-gray-50"
                >
                  <span
                    className={`w-4 h-4 flex items-center justify-center rounded border ${appliedSet.has(tag.id) ? "bg-gray-700 border-gray-700 text-white" : "border-gray-300"}`}
                  >
                    {appliedSet.has(tag.id) && (
                      <FontAwesomeIcon icon={faCheck} className="w-2.5 h-2.5" />
                    )}
                  </span>
                  <span
                    className={`flex-1 min-w-0 truncate px-2 py-1 rounded text-xs font-semibold text-left ${tagBg[tag.color]} ${tagText[tag.color]}`}
                  >
                    {tag.name}
                  </span>
                </button>
                <button
                  onClick={() => setMode({ kind: "edit", tag })}
                  className="text-gray-400 hover:text-black p-1"
                  aria-label={`Tag ${tag.name} bewerken`}
                >
                  <FontAwesomeIcon icon={faPencil} className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
          <button
            onClick={() => setMode({ kind: "create" })}
            className="w-full mt-1 px-2 py-2 text-sm text-green border border-gray-200 rounded hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Nieuw label aanmaken
          </button>
        </div>
      )}

      {mode.kind === "create" && (
        <TagForm
          submitLabel="Aanmaken"
          onCancel={() => setMode({ kind: "list" })}
          onSubmit={async (name, color) => {
            const tag = await createTag(name, color);
            if (tag) {
              setParcelTags(projectId, parcel.id, [...appliedIds, tag.id]);
              setMode({ kind: "list" });
            }
          }}
        />
      )}

      {mode.kind === "edit" && (
        <TagForm
          initialName={mode.tag.name}
          initialColor={mode.tag.color}
          submitLabel="Opslaan"
          onCancel={() => setMode({ kind: "list" })}
          onDelete={async () => {
            if (!confirm(`Label "${mode.tag.name}" verwijderen uit alle percelen?`)) return;
            await deleteTag(mode.tag.id);
            setMode({ kind: "list" });
          }}
          onSubmit={async (name, color) => {
            await updateTag(mode.tag.id, name, color);
            setMode({ kind: "list" });
          }}
        />
      )}
    </div>,
    document.body
  );
};

type FormProps = {
  initialName?: string;
  initialColor?: TagColor;
  submitLabel: string;
  onSubmit: (name: string, color: TagColor) => void | Promise<void>;
  onCancel: () => void;
  onDelete?: () => void | Promise<void>;
};

const TagForm = ({
  initialName = "",
  initialColor = "green",
  submitLabel,
  onSubmit,
  onCancel,
  onDelete,
}: FormProps) => {
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState<TagColor>(initialColor);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    await onSubmit(name.trim(), color);
    setBusy(false);
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Naam</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-2 py-1 text-sm border rounded"
          maxLength={64}
        />
      </div>
      <div>
        <div className="text-xs font-semibold text-gray-600 mb-1">Kleur</div>
        <div className="grid grid-cols-5 gap-2">
          {TAG_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`h-7 rounded ${tagBg[c]} ${tagHover[c]} flex items-center justify-center ${color === c ? "ring-2 ring-offset-1 ring-black" : ""}`}
              aria-label={`Kleur ${c}`}
            >
              {color === c && (
                <FontAwesomeIcon icon={faCheck} className={`w-3 h-3 ${tagText[c]}`} />
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between pt-1">
        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
            Verwijder
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1 text-sm text-gray-600 hover:text-black"
          >
            Annuleer
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="px-3 py-1 bg-green text-white rounded text-sm hover:bg-dark-green disabled:opacity-50"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
};
